import { makeAutoObservable } from 'mobx';
import IAttachment from '../interfaces/IAttachment';
import IGeoCoordinate from '../interfaces/IGeoCoordinate';
import ILead, { ILeadCreateState } from '../interfaces/ILead';
import ILeadNote from '../interfaces/ILeadNote';
import ILoadingProps, { ILoadingPropsDispatcher } from '../interfaces/ILoadingProps';
import LeadsApiService from '../services/leadsApiService';
import { isExcelFile } from '../utils/utils';

export default class LeadStore {
    leads: ILead[] = [];
    isLeadsLoading: ILoadingProps = {};
    editedLeadId: number | null = null;
    leadIdsAttachmentLists: { [key: number]: IAttachment[] } = {};

    constructor(
        private leadsApiService: LeadsApiService,
    ) {
        makeAutoObservable(this);
    }

    setLeads(value: ILead[]) {
        this.leads = value;
    }

    setEditedLeadId(value: number | null) {
        this.editedLeadId = value;
    }

    setLeadAttachments(value: IAttachment[], leadId: number) {
        this.leadIdsAttachmentLists[leadId] = value;
        this.leads.find(l => l.id === leadId)!.hasExcelAttachments = value.some(a => isExcelFile(a.fileName));
    }

    setIsLeadsLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isLeadsLoading.fetching = value,
            creating: (value: boolean) => this.isLeadsLoading.creating = value,
            updating: (value: boolean) => this.isLeadsLoading.updating = value,
            deleting: (value: boolean) => this.isLeadsLoading.deleting = value,
        };
    }

    addLeadNote(note: ILeadNote) {
        this.leads.find(l => l.id === note.leadId)!.notes.push(note);
    }

    initialize() {
        this.setIsLeadsLoading().fetching(true);
        this.leadsApiService.getLeads().then(leads => {
            this.setLeads(leads);
        }).finally(() => {
            this.setIsLeadsLoading().fetching(false);
        });
    }

    async createLead(leadState: ILeadCreateState, centerPoint: IGeoCoordinate, parcelId: string, pipedriveCompanyId: number | null): Promise<number> {
        this.setIsLeadsLoading().creating(true);
        try {
            const createLeadResponse = await this.leadsApiService.createLead(leadState, centerPoint, parcelId, pipedriveCompanyId);

            const notes = leadState.note
                ? [await this.leadsApiService.createLeadNote(createLeadResponse.id, leadState.note)]
                : []

            this.setLeads([
                ...this.leads,
                {
                    ...leadState,
                    centerPoint: centerPoint,
                    pipedriveCompanyId: pipedriveCompanyId,
                    parcelId: parcelId,
                    activityId: null,
                    id: createLeadResponse.id,
                    city: createLeadResponse.city,
                    state: createLeadResponse.state,
                    zip: createLeadResponse.zip,
                    saleDate: createLeadResponse.saleDate,
                    salePrice: createLeadResponse.salePrice,
                    locationGrade: createLeadResponse.locationGrade,
                    propertyGrade: null,
                    usableArea: null,
                    modifiedByUser: createLeadResponse.modifiedByUser,
                    modifiedDate: createLeadResponse.modifiedDate,
                    notes,
                    hasExcelAttachments: false,
                },
            ]);

            return createLeadResponse.id;
        } finally {
            this.setIsLeadsLoading().creating(false);
        }
    }

    async updateLead(lead: ILead) {
        this.setIsLeadsLoading().updating(true);
        try {
            const response = await this.leadsApiService.updateLead(lead);
            lead.modifiedDate = response.modifiedDate;
            lead.modifiedByUser = response.modifiedByUser;

            this.setLeads([
                ...this.leads.filter(l => l.id !== lead.id),
                lead,
            ]);
        } finally {
            this.setIsLeadsLoading().updating(false);
        }
    }

    async deleteLead(id: number) {
        this.setIsLeadsLoading().deleting(true);
        try {
            await this.leadsApiService.deleteLead(id);
            this.setLeads(this.leads.filter(l => l.id !== id));
        } finally {
            this.setIsLeadsLoading().deleting(false);
        }
    }

    async createLeadNote(leadId: number, text: string): Promise<void> {
        const createdNote = await this.leadsApiService.createLeadNote(leadId, text);
        this.addLeadNote(createdNote);
    }

    async deleteLeadNote(leadId: number, noteId: number): Promise<void> {
        await this.leadsApiService.deleteLeadNote(noteId);
        const lead = this.leads.find(l => l.id === leadId)!;
        lead.notes = lead.notes.filter(n => n.id !== noteId);
    }

    async fetchLeadAttachments(leadId: number): Promise<void> {
        const attachments = await this.leadsApiService.getLeadAttachments(leadId);
        this.setLeadAttachments(attachments, leadId);
    }

    async uploadAttachment(leadId: number, file: File): Promise<void> {
        const attachment = await this.leadsApiService.uploadLeadAttachment(leadId, file);
        this.setLeadAttachments([
            ...this.leadIdsAttachmentLists[leadId] || [],
            attachment,
        ], leadId);
    }

    async deleteAttachment(leadId: number, key: string): Promise<void> {
        await this.leadsApiService.deleteLeadAttachment(leadId, key);
        this.setLeadAttachments(this.leadIdsAttachmentLists[leadId].filter(a => a.key !== key), leadId);
    }

    clear() {
        this.setLeads([]);
        this.isLeadsLoading = {};
        this.setEditedLeadId(null);
        this.leadIdsAttachmentLists = {};
    }
}
